/**
 * Ticket List Component
 * Displays a list of tickets with loading and empty states
 */

import { Box, Typography, CircularProgress, Alert } from '@mui/material'
import { ConfirmationNumber } from '@mui/icons-material'
import { TicketSummaryDto } from '@api/types/tickets'
import { TicketCard } from './TicketCard'

interface TicketListProps {
  tickets: TicketSummaryDto[]
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  onTicketClick?: (ticketId: string) => void
  showAssignee?: boolean
}

export function TicketList({
  tickets,
  loading = false,
  error = null,
  emptyMessage = 'No tickets found',
  onTicketClick,
  showAssignee = false,
}: TicketListProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  if (tickets.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          py: 4,
        }}
      >
        <ConfirmationNumber sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {tickets.map((ticket) => (
        <TicketCard key={ticket.id} ticket={ticket} onClick={onTicketClick} showAssignee={showAssignee} />
      ))}
    </Box>
  )
}
